import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AudioOutlined,
  CaretRightOutlined,
  DownloadOutlined,
  PauseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";

let activeAudioElement = null;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const buildWaveform = () =>
  Array.from({ length: 32 }, (_, index) => {
    const base = 30 + Math.abs(Math.sin(index * 0.65)) * 45;
    const jitter = ((index * 17) % 23);
    return Math.min(95, Math.max(18, base + jitter));
  });

const AudioMessagePlayer = ({
  src,
  isOutgoing = false,
  loading = false,
  onRequestLoad,
  onDownload,
  isDownloading = false,
  compact = false,
  onPlaybackError,
}) => {
  const audioRef = useRef(null);
  const waveform = useMemo(() => buildWaveform(), []);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const playheadIndex = Math.floor((progress / 100) * (waveform.length - 1));

  const resetPlayback = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  useEffect(() => {
    resetPlayback();
    setDuration(0);
    setReady(false);
  }, [src, resetPlayback]);

  useEffect(() => {
    return () => {
      if (activeAudioElement === audioRef.current) {
        activeAudioElement = null;
      }
    };
  }, []);

  const togglePlay = async () => {
    if (!src) {
      onRequestLoad?.();
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (playing) {
      audio.pause();
      setPlaying(false);
      if (activeAudioElement === audio) {
        activeAudioElement = null;
      }
      return;
    }

    if (activeAudioElement && activeAudioElement !== audio) {
      activeAudioElement.pause();
    }

    try {
      await audio.play();
      activeAudioElement = audio;
      setPlaying(true);
    } catch (error) {
      console.error("Audio playback failed:", error);
      setPlaying(false);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  };

  const accent = isOutgoing ? "#25d366" : "#fc6b6b";
  const iconBg = isOutgoing ? "#e7fce8" : "#fce8e8";
  const barIdle = isOutgoing ? "#9ed8a8" : "#c5c9cc";
  const barPlayed = isOutgoing ? "#1fa855" : "#667781";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: compact ? 0 : 260,
        maxWidth: compact ? "100%" : 320,
        width: compact ? "100%" : undefined,
        padding: "4px 2px",
        userSelect: "none",
      }}
    >
      <button
        type="button"
        onClick={togglePlay}
        disabled={loading}
        aria-label={playing ? "Pause audio" : "Play audio"}
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "none",
          background: isOutgoing ? "#fff" : "#f0f2f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: loading ? "wait" : "pointer",
          flexShrink: 0,
          color: "#54656f",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        {loading ? (
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 16 }} />} />
        ) : playing ? (
          <PauseOutlined style={{ fontSize: 16 }} />
        ) : (
          <CaretRightOutlined style={{ fontSize: 16, marginLeft: 2 }} />
        )}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          onClick={handleSeek}
          onKeyDown={(event) => {
            if (!audioRef.current || !duration) {
              return;
            }
            if (event.key === "ArrowRight") {
              audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 1);
            }
            if (event.key === "ArrowLeft") {
              audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 1);
            }
          }}
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            height: 28,
            cursor: src ? "pointer" : "default",
          }}
        >
          {waveform.map((height, index) => {
            const isPlayed = index <= playheadIndex;
            return (
              <span
                key={index}
                style={{
                  flex: 1,
                  maxWidth: 3,
                  height: `${height}%`,
                  borderRadius: 2,
                  background: isPlayed ? barPlayed : barIdle,
                  transition: "background 0.1s ease",
                }}
              />
            );
          })}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: "#667781",
            lineHeight: 1,
          }}
        >
          {formatTime(playing || currentTime > 0 ? currentTime : duration)}
        </div>
      </div>

      {!isOutgoing && onDownload ? (
        <button
          type="button"
          onClick={onDownload}
          disabled={loading || isDownloading}
          aria-label="Download audio"
          title="Download audio"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "none",
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: accent,
            cursor: loading || isDownloading ? "wait" : "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {isDownloading ? (
            <LoadingOutlined spin style={{ fontSize: 16 }} />
          ) : (
            <DownloadOutlined style={{ fontSize: 16 }} />
          )}
        </button>
      ) : (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AudioOutlined style={{ fontSize: 20, color: accent }} />
        </div>
      )}

      {src ? (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onLoadedMetadata={(event) => {
            setDuration(event.currentTarget.duration || 0);
            setReady(true);
          }}
          onTimeUpdate={(event) => {
            setCurrentTime(event.currentTarget.currentTime || 0);
          }}
          onEnded={() => {
            setPlaying(false);
            setCurrentTime(0);
            if (activeAudioElement === audioRef.current) {
              activeAudioElement = null;
            }
          }}
          onPause={() => {
            if (audioRef.current?.currentTime === 0 || audioRef.current?.ended) {
              setPlaying(false);
            }
          }}
          onError={() => {
            onPlaybackError?.();
          }}
          style={{ display: "none" }}
        />
      ) : null}

      {!ready && !loading && !src ? (
        <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
          Tap play to load audio
        </span>
      ) : null}
    </div>
  );
};

export default AudioMessagePlayer;
